import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress, Table, TableHead, TableBody,
  TableRow, TableCell,
} from '@mui/material';
import { Refresh as RefreshIcon, Insights as InsightsIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';

const API_URL = process.env.REACT_APP_API_URL;

const when = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const rate = (r) => `${Math.round((r || 0) * 100)}%`;

/**
 * Per-ad Click-to-WhatsApp attribution, "which ad actually books appointments".
 * Each row is one ad: clicks (ad-driven conversations), bookings (flow completions
 * attributed to that ad), and the completion rate. Data comes from
 * GET /api/environments/{id}/attribution/ads.
 */
const AttributionPage = () => {
  const { authenticatedFetch, isDemoMode } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const envId = selectedEnvironment?.id;

  const load = useCallback(async () => {
    if (!envId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`${API_URL}/api/environments/${envId}/attribution/ads`);
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      setRows(await res.json());
    } catch (err) {
      setError(err.message || 'Failed to load ad attribution');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, envId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <InsightsIcon color="primary" />
        <Typography variant="h5" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Ad performance
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Which Click-to-WhatsApp ads turn into bookings. Each ad shows the conversations it
        started and how many completed a flow.
      </Typography>

      {isDemoMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Example data</strong>, this is what the page looks like with ads running. Your
          own appear here once you connect a WhatsApp number and run Click-to-WhatsApp ads.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!envId ? (
        <Alert severity="info">Select an environment to see its ad performance.</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          No ad data yet. Performance appears here once customers arrive via a Click-to-WhatsApp
          ad and complete one of your flows.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ad</TableCell>
                <TableCell align="right">Clicks</TableCell>
                <TableCell align="right">Bookings</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell>Last click</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.adId || 'unattributed'} hover>
                  <TableCell>
                    <Typography variant="body2">{r.headline || 'Untitled ad'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {r.adId || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{r.clicks}</TableCell>
                  <TableCell align="right">{r.completions}</TableCell>
                  <TableCell align="right">{rate(r.completionRate)}</TableCell>
                  <TableCell>
                    <Typography variant="caption">{when(r.lastClickAt)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default AttributionPage;
